import {
    EVENTBRITE_REFINEMENT_TO_CATEGORY,
    SUBCATEGORY_REFINEMENT_TO_KEY,
} from '@eventbrite/categories';
import { History, Location } from 'history';
import React from 'react';
import { match } from 'react-router-dom';
import { FeatureFlags } from '../../types';
import { LocationProvider, RouteProvider, SearchProvider } from './context';
import { useLocationByLocationSlug } from './hooks/queries/location';
import { parseEventSearchFromUrlParams } from './utils';
export interface CategoryBrowseRouteParams {
    locationSlug: string;
    category: string;
    subcategory: string;
}
export type CategoryBrowseRouteMatch = match<CategoryBrowseRouteParams>;
export interface CategoryBrowseProviderProps {
    match: match<CategoryBrowseRouteParams>;
    location: Location;
    history: History;
    location_slug: string;
    location_data: {
        displayLocation: string;
        city: string;
        region: string;
        country: string;
        latitude: number;
        longitude: number;
        isOnline: boolean;
        placeId: string;
        placeType: string;
        countrySlug?: string;
    };
    featureFlags?: FeatureFlags;
}

const getSearchParams = (
    location: Location,
    match: CategoryBrowseRouteMatch,
) => {
    const params = new URLSearchParams(location.search);
    let { activeTag, date, customDate } = parseEventSearchFromUrlParams(params);

    if (!activeTag) {
        activeTag = EVENTBRITE_REFINEMENT_TO_CATEGORY[match.params.category];
        if (match.params.subcategory) {
            activeTag =
                SUBCATEGORY_REFINEMENT_TO_KEY[activeTag][
                    match.params.subcategory
                ];
        }
    }

    return { activeTag, date, customDate };
};

export const CategoryBrowseProviders: React.FunctionComponent<
    CategoryBrowseProviderProps
> = (props) => {
    const { match, location, history, location_data, featureFlags } = props;

    const { activeTag, date, customDate } = getSearchParams(location, match);

    const initialLocation = location_data
        ? {
              currentPlace:
                  location_data?.displayLocation || location_data?.city,
              currentPlaceParent:
                  location_data?.displayLocation === location_data?.region
                      ? location_data?.country
                      : location_data?.region,
              slug: match.params.locationSlug,
              latitude: location_data?.latitude,
              longitude: location_data?.longitude,
              isOnline: location_data?.isOnline,
              placeId: location_data?.placeId,
              placeType: location_data?.placeType,
              country: location_data?.country,
              countrySlug: location_data?.countrySlug,
          }
        : {};

    //Initialize location if hydrated via props
    useLocationByLocationSlug(match.params.locationSlug, initialLocation);

    return (
        <RouteProvider
            match={match}
            location={location}
            history={history}
            parsedValues={{
                activeTag,
                date,
                customDate,
            }}
            featureFlags={featureFlags}
        >
            <LocationProvider locationSlug={match.params.locationSlug}>
                <SearchProvider
                    initialQuery={{
                        tags: [activeTag || ''],
                        dates:
                            date === 'current_future'
                                ? ['current_future']
                                : ['current_future', date],
                        date_range: customDate,
                    }}
                >
                    {props.children}
                </SearchProvider>
            </LocationProvider>
        </RouteProvider>
    );
};
